/**
 * JurisdictionCard Component
 * Displays AHJ (Authority Having Jurisdiction) information with overlay badges
 *
 * Part of Sprint Day 5-6: Compliance Dashboard UI
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Phone,
  Mail,
  Globe,
  ExternalLink,
  ShieldAlert,
  Waves,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Wind
} from "lucide-react";
import type { Jurisdiction } from "@/hooks/useCompliance";

interface JurisdictionCardProps {
  jurisdiction: Jurisdiction;
  compact?: boolean;
}

export function JurisdictionCard({ jurisdiction, compact = false }: JurisdictionCardProps) {
  const overlayBadges: Array<{
    label: string;
    variant: "destructive" | "secondary" | "outline" | "default";
    icon: typeof ShieldAlert;
    description: string;
  }> = [];

  if (jurisdiction.overlays?.highVelocityHurricaneZone) {
    overlayBadges.push({
      label: 'HVHZ',
      variant: 'destructive',
      icon: ShieldAlert,
      description: 'High Velocity Hurricane Zone - Enhanced wind requirements'
    });
  }
  if (jurisdiction.overlays?.floodZone) {
    overlayBadges.push({
      label: 'Flood Zone',
      variant: 'secondary',
      icon: Waves,
      description: 'Flood Zone - Elevation and flood-proofing requirements'
    });
  }
  if (jurisdiction.overlays?.coastalZone) {
    overlayBadges.push({
      label: 'Coastal',
      variant: 'secondary',
      icon: MapPin,
      description: 'Coastal Zone - Salt air and corrosion requirements'
    });
  }
  if (jurisdiction.overlays?.windBorneDebrisRegion) {
    overlayBadges.push({
      label: 'WBDR',
      variant: 'outline',
      icon: Wind,
      description: 'Wind-Borne Debris Region - Impact-rated product requirements'
    });
  }

  // Compact version for inline display
  if (compact) {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">{jurisdiction.ahjName}</span>
        {overlayBadges.map((badge, i) => (
          <Badge key={i} variant={badge.variant} className="text-xs">
            <badge.icon className="h-3 w-3 mr-1" />
            {badge.label}
          </Badge>
        ))}
        {jurisdiction.verificationStatus === 'verified' && (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        )}
      </div>
    );
  }

  // Full card version
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{jurisdiction.ahjName}</CardTitle>
          </div>
          {jurisdiction.verificationStatus === 'verified' ? (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Verified
            </Badge>
          ) : jurisdiction.verificationStatus === 'stale' ? (
            <Badge variant="outline" className="text-orange-600 border-orange-600">
              <AlertCircle className="h-3 w-3 mr-1" />
              Stale Data
            </Badge>
          ) : (
            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
              <AlertCircle className="h-3 w-3 mr-1" />
              {jurisdiction.verificationStatus || 'Pending'}
            </Badge>
          )}
        </div>
        {jurisdiction.ahjShortCode && (
          <p className="text-sm text-muted-foreground">
            Code: {jurisdiction.ahjShortCode} | Level: {jurisdiction.scopeLevel}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Overlay Badges */}
        {overlayBadges.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Special Requirements
            </p>
            <div className="flex flex-wrap gap-2">
              {overlayBadges.map((badge, i) => (
                <Badge
                  key={i}
                  variant={badge.variant}
                  className="flex items-center gap-1"
                  title={badge.description}
                >
                  <badge.icon className="h-3 w-3" />
                  {badge.label}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Contact Information
          </p>
          <div className="space-y-2 text-sm">
            {jurisdiction.phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`tel:${jurisdiction.phone}`}
                  className="hover:underline text-primary"
                >
                  {jurisdiction.phone}
                </a>
              </div>
            )}
            {jurisdiction.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a
                  href={`mailto:${jurisdiction.email}`}
                  className="hover:underline text-primary"
                >
                  {jurisdiction.email}
                </a>
              </div>
            )}
            {jurisdiction.website && (
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <a
                  href={jurisdiction.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline flex items-center gap-1 text-primary"
                >
                  Website <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Permit Portal Button */}
        {jurisdiction.portalUrl && (
          <Button variant="outline" className="w-full" asChild>
            <a
              href={jurisdiction.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Permit Portal
            </a>
          </Button>
        )}

        {/* No portal available message */}
        {!jurisdiction.portalUrl && (
          <p className="text-xs text-muted-foreground text-center">
            No online permit portal available for this jurisdiction
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default JurisdictionCard;
